import { z } from 'zod';
// ---------------------------------------------------------------------------
// Additional procedures (Domain 4 — Cockpit, REQ-VIS-040/041, 044…050)
// ---------------------------------------------------------------------------
/**
 * One entry of the additional-procedure catalogue of a trial (REQ-VIS-040).
 * `trialVisitId` null = a FREE procedure ("procedura wolna"); a value points
 * at a protocol visit position ("procedura wizytowa").
 *
 * The canonical object names are still open (`Q-VIS-005`, `Q-VIS-010`) — the
 * DTO keeps the neutral names of the schema (`trial_additional_tasks`).
 */
export const TrialAdditionalTaskDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    trialId: z.number().int().positive(),
    /** Protocol visit position; null = free procedure (REQ-VIS-040). */
    trialVisitId: z.number().int().positive().nullable(),
    name: z.string().min(1).max(255),
    comment: z.string().nullable(),
    /** "Data obowiązywania" (REQ-VIS-040) — optional validity start. */
    validFrom: z.iso.date().nullable(),
    /** "Czy wymaga rejestracji personelu" (REQ-VIS-040/041). */
    requiresPersonnel: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Request of `POST /api/v1/visits/procedure-catalogue` (REQ-VIS-040). The trial
 * is validated in Domain 2; a `trialVisitId` that belongs to another trial is a
 * 400. `name` is required — a procedure without a name is not storable.
 */
export const CreateTrialAdditionalTaskRequestSchema = z.object({
    trialId: z.coerce.number().int().positive(),
    name: z.string().trim().min(1).max(255),
    trialVisitId: z.coerce.number().int().positive().nullable().optional(),
    comment: z.string().max(2000).nullable().optional(),
    validFrom: z.iso.date().nullable().optional(),
    requiresPersonnel: z.boolean().optional(),
});
/** Query of `GET /api/v1/visits/procedure-catalogue` (REQ-VIS-040/050). */
export const TrialAdditionalTasksQuerySchema = z.object({
    trialId: z.coerce.number().int().positive(),
});
/**
 * One execution of an additional procedure on a patient visit (REQ-VIS-041).
 *
 * `fromPattern` is the deletion discriminator of REQ-VIS-048: only a row with
 * `false` (added by hand outside the visit pattern, REQ-VIS-050) may be
 * removed; a pattern row is unticked instead (`done: false`), never deleted
 * (REQ-VIS-049).
 */
export const PatientAdditionalTaskDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    /** The Domain 3 visit this execution supplements — always required. */
    patientVisitId: z.number().int().positive(),
    catalogId: z.number().int().positive(),
    fromPattern: z.boolean(),
    /** Completion state, never a deletion flag (REQ-VIS-049). */
    done: z.boolean(),
    performedDate: z.iso.date().nullable(),
    /** Multiplicity of the working list (REQ-VIS-045). */
    count: z.number().int().min(1),
    /** Parent position of a variant group (REQ-VIS-046). */
    parentId: z.number().int().positive().nullable(),
    personnelId: z.number().int().positive().nullable(),
    comment: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** Query of `GET /api/v1/visits/:visitId/procedure-executions` (REQ-VIS-045 step 2). */
export const ProcedureExecutionsQuerySchema = z.object({
    patientVisitId: z.coerce.number().int().positive(),
});
/**
 * One line of the WORKING LIST of a visit (REQ-VIS-045/046) — the already
 * saved executions joined with the visit pattern, ready for the checkbox tree.
 *
 * `key` is the saved execution's row id, or `null` for a pattern position that
 * is not saved yet (`count`/`done` then report the pattern defaults). The
 * `procedureKey`/`templateKey` pair keeps the neutral vocabulary of the shared
 * rule (`Q-VIS-005`, `Q-VIS-011`): a line WITHOUT `templateKey` sits outside
 * the pattern and is the ONLY removable kind (REQ-VIS-048).
 */
export const ProcedureWorkLineDtoSchema = z.object({
    procedureKey: z.string().min(1),
    /** Row id of the saved execution; null when the line is not saved yet. */
    key: z.string().nullable(),
    catalogId: z.number().int().positive(),
    name: z.string().min(1).max(255),
    /** Pattern position this line belongs to; null = added outside the pattern. */
    templateKey: z.string().nullable(),
    count: z.number().int().min(1),
    done: z.boolean(),
    performedDate: z.iso.date().nullable(),
    comment: z.string().nullable(),
    /** REQ-VIS-048: only a line outside the pattern may be deleted. */
    removable: z.boolean(),
});
/** Query of the working list of one visit (REQ-VIS-045). */
export const ProcedureWorkListQuerySchema = z.object({
    patientVisitId: z.coerce.number().int().positive(),
});
/**
 * Request of the tick/untick of one execution (REQ-VIS-044, REQ-VIS-049).
 * Only the completion state is writable from here: the default procedure DATE
 * is computed by the server from the visit's own dates, so the client never
 * dictates it. Unticking never deletes the row (REQ-VIS-049).
 */
export const SetProcedureExecutionDoneRequestSchema = z.object({
    done: z.boolean(),
});
/**
 * Request of `POST /api/v1/visits/procedure-executions` (REQ-VIS-050): adds a
 * catalogue procedure OUTSIDE the visit pattern. The stored row is born
 * `fromPattern: false`, which is what makes it removable (REQ-VIS-048).
 *
 * `patientVisitId` is required, so an execution can never exist outside a visit
 * (REQ-VIS-041); the composite foreign key protects the tenant.
 */
export const AddOutsidePatternProcedureRequestSchema = z.object({
    patientVisitId: z.coerce.number().int().positive(),
    catalogId: z.coerce.number().int().positive(),
    comment: z.string().max(2000).nullable().optional(),
    personnelId: z.coerce.number().int().positive().nullable().optional(),
});
/**
 * One line of the DRAFT of the working list being saved (REQ-VIS-045 step 6).
 *
 * A line WITHOUT `key` is a not-yet-saved position (the `key: null` rows of
 * `GET procedure-work-list`) and gets inserted; a line WITH `key` points at an
 * existing execution, which is refreshed in place and NEVER deleted
 * (REQ-VIS-049). `done` is the tick of the draft and is only meaningful for a
 * position that is being inserted — the tick of an already-saved line travels
 * through `PATCH procedure-executions/:id`.
 */
export const ProcedureDraftLineRequestSchema = z.object({
    /** Row id of a saved execution; null/absent = a position to insert. */
    key: z.string().trim().min(1).nullable().optional(),
    /** Catalogue entry of the line — the only identity of a new row. */
    catalogId: z.coerce.number().int().positive(),
    /** Tick of the draft; ignored for a line that already exists. */
    done: z.boolean().optional(),
    comment: z.string().max(2000).nullable().optional(),
});
/**
 * Request of `PUT /api/v1/visits/:visitId/procedure-draft` (REQ-VIS-045 step 6,
 * REQ-VIS-049): persists the working list of one visit.
 *
 * Saving NEVER deletes: a position the draft does not carry stays in the table
 * exactly as it was, which is what keeps an unticked pattern line provable.
 * The path carries the patient visit, so no line can be stored on another
 * visit than the one that was opened, and the procedure DATE of a tick is
 * still computed by the server from the visit (REQ-VIS-044) — the client
 * never dictates it.
 */
export const SaveProcedureDraftRequestSchema = z.object({
    /** The whole draft of the visit, in the order it is shown. Capped at 500. */
    lines: z.array(ProcedureDraftLineRequestSchema).max(500),
});
//# sourceMappingURL=procedures.schema.js.map